/**
 * LooseDiamondsInformation custom element for handling loose diamonds information
 * 
 * @module loose-diamonds-information
 * @version 1.0.0
 * @extends HTMLElement
 */
class LooseDiamondsInformation extends HTMLElement {

  /**
   * Constructor for the LooseDiamondsInformation element
   */
  constructor() {

    super();

    this.data = {};

    // Grade copy is Allurez's own, from slides 113-115 of the review deck
    // ("Text for Diamond Color / Clarity / Cut"). The deck writes each entry as
    // a reference list ("VVS1 + VVS2 - ..."); the grade already appears in the
    // accordion heading, so the leading grade prefix is dropped here.
    this.descriptions = {
      cut: {
        'IDEAL': 'Maximum brilliance and fire. Ideal proportions and light performance. Highest value and most desirable.',
        'IDEAL+HEARTS': 'Maximum brilliance and fire. Ideal proportions and light performance. Highest value and most desirable.',
        '8X': 'Maximum brilliance and fire. Ideal proportions and light performance. Highest value and most desirable.',
        'EXCELLENT': 'Maximum brilliance and fire. Ideal proportions and light performance. Highest value and most desirable.',
        'VERY GOOD': 'Very strong sparkle, minor proportion differences from excellent. A great value alternative.',
        'VG': 'Very strong sparkle, minor proportion differences from excellent. A great value alternative.',
        'GOOD': 'Can have a noticeable reduction in sparkle compared to higher grades. Still acceptable appearance.',
        'GD': 'Can have a noticeable reduction in sparkle compared to higher grades. Still acceptable appearance.',
        'G': 'Can have a noticeable reduction in sparkle compared to higher grades. Still acceptable appearance.',
        'FAIR': 'Reduced brilliance and life. Light leakage might be present.',
        'FR': 'Reduced brilliance and life. Light leakage might be present.',
        'POOR': 'Significant light loss. Dull or glassy appearance.'
      },
      color: {
        'D': 'D is completely colorless. This is the total absence of color. Extremely rare, highest value.',
        'E': 'E-F is still colorless. These are still amazing values.',
        'F': 'E-F is still colorless. These are still amazing values.',
        'G': 'G-H-I is near colorless. These are still good values. You cannot see color in this range without being trained.',
        'H': 'G-H-I is near colorless. These are still good values. You cannot see color in this range without being trained.',
        'I': 'G-H-I is near colorless. These are still good values. You cannot see color in this range without being trained.',
        'J': 'J-K - There is a slight tint.',
        'K': 'J-K - There is a slight tint.'
      },
      clarity: {
        'FL': 'Flawless. There are no inclusions or blemishes under 10x magnification. This is extremely rare and valuable.',
        'FL/VVS': 'Flawless. There are no inclusions or blemishes under 10x magnification. This is extremely rare and valuable.',
        'IF': 'Internally Flawless. Internally the stone is flawless but there are minor surface blemishes possible.',
        'VVS1': 'Very Very Slightly Included. Minute inclusions are extremely difficult to see at 10x magnification.',
        'VVS2': 'Very Very Slightly Included. Minute inclusions are extremely difficult to see at 10x magnification.',
        'VS1': 'Very Slightly Included. Minor inclusions are difficult to see at 10x magnification.',
        'VS2': 'Very Slightly Included. Minor inclusions are difficult to see at 10x magnification.',
        'SI1': 'Slightly Included. There are noticeable inclusions at 10x magnification. Usually these inclusions are not able to see with the naked eye.',
        'SI2': 'Slightly Included. There are noticeable inclusions at 10x magnification. Usually these inclusions are not able to see with the naked eye.',
        'SI3': 'Slightly Included. There are noticeable inclusions at 10x magnification. Usually these inclusions are not able to see with the naked eye.',
        'I1': 'Included. These diamonds might have inclusions that are visible to the naked eye.',
        'I2': 'Included. These diamonds might have inclusions that are visible to the naked eye.',
        'I3': 'Included. These diamonds might have inclusions that are visible to the naked eye.'
      },
      report: {
        'GIA': 'GIA, the Gemological Institute of America, is the leading gemological laboratory in the world. It is the industry’s most trusted and most widely used service.',
        'AGS': 'AGS stands for American Gemological Society and it is also a US based laboratory (with main offices in Las Vegas). The AGS laboratory is renowned for their scientific approach and research into diamond cut grading.',
        'EGL': 'EGL is an abbreviation for European Gemological Laboratory. It is a European founded grading agency and has a big global presence – with offices in London, Paris, Tel Aviv, Tokyo, Johannesburg, among others.',
        'IGI': 'IGI is the International Gemological Institute that is based in Antwerp, Belgium. After GIA, they are actually the 2nd most well-known lab in the world. A significant amount of polished diamonds in the market are actually graded by IGI and they are very popular in Asian countries.'
      }
    };

    // Intro shown above the Cut scale (slide 115).
    this.intros = {
      cut: 'Cut is how well a diamond’s proportions, symmetry, and polish interact with light. This will affect its brilliance, fire and sparkle. It is often considered the most important of the 4Cs.'
    };

    // Grade scales shown under the Cut / Color / Clarity accordions. Ranges follow
    // slides 113-115: colour runs D→K, clarity FL→I1, cut Excellent→Poor. Swatch
    // artwork is still served from the legacy CDN (it 404s on www.allurez.com — the
    // images. host is the live one). A cell with no `image` renders label-only:
    // the legacy set has no FL, I1 or standalone K artwork.
    this.scales = {
      cut: {
        note: 'Based on the GIA grading scale.',
        width: 96,
        height: 73,
        cells: [
          { label: 'Excellent', image: 'excellent', grades: ['EXCELLENT', 'IDEAL', 'IDEAL+HEARTS', '8X'] },
          { label: 'Very Good', image: 'verygood', grades: ['VERY GOOD', 'VG'] },
          { label: 'Good', image: 'good', grades: ['GOOD', 'GD', 'G'] },
          { label: 'Fair', image: 'fair', grades: ['FAIR', 'FR'] },
          { label: 'Poor', image: 'poor', grades: ['POOR'] }
        ],
        groups: []
      },
      color: {
        note: 'Based on the GIA grading scale.',
        width: 96,
        height: 73,
        cells: [
          { label: 'D', image: 'color/color_d', grades: ['D'] },
          { label: 'E', image: 'color/color_e', grades: ['E'] },
          { label: 'F', image: 'color/color_f', grades: ['F'] },
          { label: 'G', image: 'color/color_g', grades: ['G'] },
          { label: 'H', image: 'color/color_h', grades: ['H'] },
          { label: 'I', image: 'color/color_i', grades: ['I'] },
          { label: 'J', image: 'color/color_j', grades: ['J'] },
          // No standalone K artwork; the K-M swatch is the same tinted stone.
          { label: 'K', image: 'color/color_km', grades: ['K'] }
        ],
        groups: [
          { label: 'Completely Colorless', span: 1 },
          { label: 'Colorless', span: 2 },
          { label: 'Near Colorless', span: 3 },
          { label: 'Slight Tint', span: 2 }
        ]
      },
      clarity: {
        note: 'Based on the GIA grading scale.',
        width: 128,
        height: 106,
        cells: [
          { label: 'FL', grades: ['FL', 'FL/VVS'] },
          { label: 'IF', image: 'clarity_if', grades: ['IF'] },
          { label: 'VVS1', image: 'clarity_vvs1', grades: ['VVS1'] },
          { label: 'VVS2', image: 'clarity_vvs2', grades: ['VVS2'] },
          { label: 'VS1', image: 'clarity_vs1', grades: ['VS1'] },
          { label: 'VS2', image: 'clarity_vs2', grades: ['VS2'] },
          { label: 'SI1', image: 'clarity_si1', grades: ['SI1'] },
          { label: 'SI2', image: 'clarity_si2', grades: ['SI2'] },
          { label: 'SI3', image: 'clarity_si3', grades: ['SI3'] },
          { label: 'I1', grades: ['I1', 'I2', 'I3'] }
        ],
        groups: []
      }
    };

    this.enableLoading();
  }

  /**
   * Render the grade scale that sits under a Cut, Color or Clarity accordion.
   *
   * Returns '' for attribute types that have no scale, so callers can inline it
   * unconditionally.
   *
   * @param {string} type - The attribute type
   * @param {string} value - The stone's grade for that attribute
   * @returns {string} The scale markup
   */
  renderScale(type, value) {

    const scale = this.scales[type];

    if (!scale) {
      return '';
    }

    const normalizedValue = (value || '').toString().toUpperCase().trim();

    // Grades off the scale (fancy colors, unrecognised cut codes) would render a
    // chart with nothing highlighted, which reads as broken. Show nothing instead.
    if (!scale.cells.some(cell => cell.grades.includes(normalizedValue))) {
      return '';
    }

    const label = { cut: 'Cut', color: 'Color', clarity: 'Clarity' }[type];
    const columns = scale.cells.length;

    const cells = scale.cells.map(cell => {

      const isActive = cell.grades.includes(normalizedValue);

      // The legacy set has no FL, I1 or standalone K swatch. Those cells keep
      // their slot and label so the scale still reads as a continuous range.
      const swatch = cell.image ? `
        <img
          src="https://images.allurez.com/images/dtl_new/${ cell.image }.${ type === 'clarity' ? 'jpg' : 'png' }"
          alt="${ label } - ${ cell.label }"
          width="${ scale.width }"
          height="${ scale.height }"
          loading="lazy"
          class="w-full h-auto" />
      ` : `<span class="block w-full" style="aspect-ratio: ${ scale.width } / ${ scale.height };"></span>`;

      return `
        <div class="flex flex-col items-center gap-2 px-1 py-2 border ${ isActive ? 'border-secondary-600' : 'border-transparent' }">
          <span class="text-xs leading-none text-center ${ isActive ? 'text-secondary-600 font-medium' : '' }">${ cell.label }</span>
          ${ swatch }
        </div>
      `;
    }).join('');

    const groups = scale.groups.length ? `
      <div class="grid gap-x-1 mt-2" style="grid-template-columns: repeat(${ columns }, minmax(0, 1fr));">
        ${ scale.groups.map(group => `
          <div class="border-t border-base-border pt-2 text-center text-[10px] uppercase leading-tight" style="grid-column: span ${ group.span } / span ${ group.span };">
            ${ group.label }
          </div>
        `).join('') }
      </div>
    ` : '';

    return `
      <div class="overflow-x-auto">
        <div class="min-w-[420px]">
          <div class="grid gap-x-1" style="grid-template-columns: repeat(${ columns }, minmax(0, 1fr));">
            ${ cells }
          </div>
          ${ groups }
        </div>
      </div>
      ${ scale.note ? `<p class="text-xs text-primary-300">${ scale.note }</p>` : '' }
    `;
  }

  /**
   * Get attribute description
   *
   * @param {string} type - The attribute type
   * @param {string} value - The attribute value
   * @returns {string} The description
   */
  getAttributeDescription(type, value) {

    if (!value) {
      return '';
    }

    const normalizedValue = value.toString().toUpperCase().trim();
    const typeDescriptions = this.descriptions[type];

    if (!typeDescriptions) {
      return '';
    }

    // Try direct match
    if (typeDescriptions[normalizedValue]) {
      return typeDescriptions[normalizedValue];
    }

    // Try case-insensitive keys
    const match = Object.keys(typeDescriptions).find(key => key.toUpperCase() === normalizedValue);
    if (match) {
      return typeDescriptions[match];
    }

    return '';
  }

  /**
   * Connected callback
   */
  async connectedCallback() {

    const params = new URL(window.location).searchParams;
    const id = params.get('id') || window.theme.stones.getIdFromSlug(params.get('stone') || '');

    this.data = await theme.stones.getStoneById(id, 'diamond');

    this.render();

    this.initEvents();

    this.disableLoading();
  }

  /**
   * Render html structure
   */
  render() {

    const item = this.data;

    if (!item) {
      return;
    }

    const itemName = window.theme.stones.getStoneTitle(item, 'diamond');

    // Update page meta title
    document.title = itemName;

    const renderAttribute = (type, label, value) => {
      
      if (!value || value.toString().toUpperCase() === 'NONE' || value.toString().trim() === '' || value.toString().trim() === '-') {
        return '';
      }

      const description = this.getAttributeDescription(type, value);
      const scale = this.renderScale(type, value);
      const labelText = `${label}: ${value}`;

      if (!description && !scale) {
        return `
          <div class="uppercase text-sm pt-[13px] pb-[12px] border-b border-base-border">
            ${ labelText }
          </div>
        `;
      }

      return `
        <component-accordion data-facet class="group w-auto border-b border-base-border">
          <div class="uppercase text-sm pt-[13px] pb-[12px] flex items-center gap-2 justify-between" data-accordion-trigger>
            ${ labelText }
            <span class="w-5 h-5 group-[.active]:hidden">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10 4.25C10.4142 4.25 10.75 4.58579 10.75 5V9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H10.75V15C10.75 15.4142 10.4142 15.75 10 15.75C9.58579 15.75 9.25 15.4142 9.25 15V10.75L5 10.75C4.58579 10.75 4.25 10.4142 4.25 10C4.25 9.58579 4.58579 9.25 5 9.25L9.25 9.25V5C9.25 4.58579 9.58579 4.25 10 4.25Z"
                  fill="currentColor" />
              </svg>
            </span>
            <span class="w-5 h-5 hidden group-[.active]:block">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M4.25 10C4.25 9.58579 4.58579 9.25 5 9.25H15C15.4142 9.25 15.75 9.58579 15.75 10C15.75 10.4142 15.4142 10.75 15 10.75H5C4.58579 10.75 4.25 10.4142 4.25 10Z"
                  fill="currentColor" />
              </svg>
            </span>
          </div>
          <div data-accordion-container data-facet-container>
            <div class="flex flex-col gap-5 pt-1 pb-5 px-1">
              ${ this.intros[type] ? `<p>${ this.intros[type] }</p>` : '' }
              ${ description ? `<p>${ description }</p>` : '' }
              ${ scale }
            </div>
          </div>
        </component-accordion>
      `;
    };

    // A callout on the measurement diagrams. Omitted entirely when the legacy
    // row has nothing usable, rather than printing "undefined".
    const spec = (className, prefix, value, suffix = '') => {

      const clean = window.theme.stones.cleanValue(value);

      return clean ? `<span class="${ className }">${ prefix }${ clean }${ suffix }</span>` : '';
    };

    // 360° frame sequence — the real one for this stone when it exists, else a
    // generic spin for its shape. Null when neither applies.
    const view360 = window.theme.stones.getStone360(item);

    this.innerHTML = `

      <div class="px-4 container">
        <h1 class="uppercase text-base mb-4 laptop:text-xl laptop:hidden">
          ${ itemName }
        </h1>
      </div>

      <div class="flex flex-col pb-5 laptop:flex-row laptop:gap-7">

        <div class="shrink-0 w-full laptop:pl-4 laptop:max-w-[620px] desktop:max-w-[884px]">

          <product-gallery class="block laptop:sticky laptop:top-40" data-navigation>
            <div class="swiper product-gallery">
              <div class="swiper-wrapper laptop:!grid laptop:!grid-cols-2 laptop:!gap-4">

                ${ view360 ? `
                <div class="swiper-slide px-4 laptop:px-0 laptop:border laptop:border-base-border">
                  ${ view360.video ? `
                  <video-360
                    src="${ view360.video }"
                    poster="${ view360.poster }"
                    label="${ itemName } 360 degree view"></video-360>` : `
                  <diamond-360
                    base="${ view360.base }"
                    sequence="${ view360.sequence }"
                    frames="${ view360.frames }"
                    alt="${ itemName }"></diamond-360>` }
                </div>` : '' }

                <div class="swiper-slide px-4 laptop:px-0 laptop:border laptop:border-base-border">
                  ${ item.video_url ? `<button class="btn btn-ghost bg-white hover:bg-white btn-sm absolute bottom-2 left-0 right-0 w-56 m-auto" data-drawer-target="360-video">360º Video</button>` : ''}
                  <img src="${ item.image }" alt="${ itemName }" class="w-full h-auto" />
                </div>

                <div class="swiper-slide dmd-para ${ item.shape.toLowerCase() } relative px-4 laptop:px-0 laptop:border laptop:border-base-border">
                  <img src="https://images.allurez.com/gemsimages/diamond_shapes/diamond/2018/${ item.shape }/large_2.gif" alt="${ itemName }" class="w-full h-auto" />
                  ${ spec('culet', 'Culet: ', item.culet) }
                  ${ spec('girdle', 'Girdle: ', theme.stones.formatGirdle(item.girdle_min, item.girdle_max)) }
                  ${ spec('depth', 'Depth:<br>', item.depth) }
                  ${ spec('depth1', 'Depth:<br>', item.depth_mm) }
                  ${ spec('dyp-table', 'Table: ', item.table_percent) }
                </div>

                <div class="swiper-slide dmd-para ${ item.shape.toLowerCase() } relative px-4 laptop:px-0 laptop:border laptop:border-base-border">
                  <img src="https://images.allurez.com/gemsimages/diamond_shapes/diamond/2018/${ item.shape }/large_3.gif" alt="${ itemName }" class="w-full h-auto" />
                  ${ spec('width', 'Width: ', item.width_mm, ' mm') }
                  ${ spec('length', 'Length: ', item.length_mm, ' mm') }
                </div>

              </div>
              <div class="swiper-pagination laptop:hidden"></div>
            </div>
          </product-gallery>

          ${ item.video_url ? `<side-drawer id="360-video" side="center" width="500px">
            <p class="text-sm text-center">360º Video - Drag to rotate</p>
            <iframe style="aspect-ratio: 1 / 1; height: auto;  width: 100%; transform: scale(0.9);" src="${ item.video_url }" height="600" width="100%" allow="autoplay" loading="lazy" frameborder="0" allowfullscreen>></iframe>
          </side-drawer>` : '' }

        </div>

        <div class="px-4 w-full flex flex-col">
          
          <div class="hidden uppercase text-base mb-5 laptop:block laptop:text-xl laptop:mb-0">${ itemName }</div>
       
          <div class="flex items-center justify-start gap-10 py-4 pt-6 mt-6 border-t border-primary-100 largemobile:gap-16 laptop:border-none laptop:mt-0">
            <div class="flex flex-col">
              <span class="text-[18px] leading-[26px] font-medium">${ theme.money.format(item.price, true) }</span>
              <span class="text-xs leading-3">${ (((item.price_compare_at - item.price) * 100) / item.price_compare_at).toFixed(0) }% Savings</span>
            </div>
            <div class="flex flex-col">
              <span class="text-primary-300 line-through text-[18px] leading-[26px]">${ theme.money.format(item.price_compare_at, true) }</span>
              <span class="text-xs leading-3">Everyone Else</span>
            </div>
            <a href="#" class="text-xs underline max-w-[80px]" data-drawer-target="fair-pricing">Learn about our fair pricing</a>
          </div>

          <side-drawer 
          id="fair-pricing"
          side="center"
          width="700px">
            <div class="flex flex-col gap-5 px-6 pb-6">
              <div class="text-2xl uppercase">Smart & Fair price</div>
              <p class="text-sm">Major brands in our industry markup their products 8-10x the actual cost. We do things differently. To make luxury accessible, we’ve built relationships with the best manufacturers and sell directly to you. Basically, our jewelry is handcrafted with the same top materials and quality craftsmanship as other luxury jewelry brands but without the traditional markups.</p>
            
              <picture>
                <source media="(min-width: 768px)" srcset="https://cdn.shopify.com/s/files/1/0624/1114/5290/files/fair_price_desktop.jpg?v=1752709815">
                <img src="https://cdn.shopify.com/s/files/1/0624/1114/5290/files/fair_price_mobile.jpg?v=1752709815" alt="Fair Pricing" width="100%" height="100%">
              </picture>
            </div>
          </side-drawer>

          <div class="mt-3 mb-5 flex gap-2">
            <div data-item-tags class="px-3 bg-[#F1E6E6] py-2 inline-flex justify-center items-center gap-2 rounded-[100px]">
              <div class="justify-start  text-xs font-medium uppercase leading-none">Sustainable</div>
            </div>
            <div data-item-tags class="px-3 bg-[#F1E6E6] py-1.5 inline-flex justify-center items-center gap-2 rounded-[100px]">
              <div class="justify-start  text-xs font-medium uppercase leading-none">Only One Available</div>
            </div>
          </div>

          ${ renderAttribute('cut', 'Cut', item.cut) }

          ${ renderAttribute('color', 'Color', item.color) }

          ${ renderAttribute('clarity', 'Clarity', item.clarity) }

          ${ renderAttribute('report', 'Graded By', item.report) }

          <form method="post" action="/cart/add" class="block mt-[30px]">
            <input type="hidden" name="id" value="${this.dataset.variantId}">
            <input type="hidden" name="quantity" value="1">

            <dyo-add type="diamond" class="mb-[6px] block" stone-id="${item.id}"></dyo-add>

            <button type="submit" data-add-to-cart-button="Buy Loose Diamond" class="btn btn-ghost w-full py-[22px]">Buy Loose Diamond</button>

          </form>
          
          ${this.innerHTML}

        </div>
      </div>
    `;
  }

  /**
   * Initialize the events
   */
  initEvents() {

    // Handle add to cart form
    this.querySelector('form[action="/cart/add"]').addEventListener('submit', async event => {

      event.preventDefault();

      const submitButton = this.querySelector('button[data-add-to-cart-button]');
      submitButton.textContent = 'Adding...';
      submitButton.disabled = true;
      
      const result = await window.theme.stones.addToCart(this.data.id, 'diamond');

      submitButton.textContent = 'Added!';

      setTimeout(() => {
        submitButton.textContent = submitButton.dataset.addToCartButton;
        submitButton.disabled = false;
      }, 2000);
    });
  }

  /**
   * Enable the loading state
   */
  enableLoading() {
    this.classList.add('loading');

    if (window.theme.loading) {
      window.theme.loading.enable();
    }
  }

  /**
   * Disable the loading state
   */
  disableLoading() {
    this.classList.remove('loading');
    this.classList.remove('min-h-screen');

    if (window.theme.loading) {
      window.theme.loading.disable();
    }
  }
}

customElements.define('loose-diamond-information', LooseDiamondsInformation);

